import { Stage } from './environment.type';

/**
 * Detects if the app is running in gateway mode by checking the base href.
 * Gateway mode means the app is served under a subpath (e.g., /analytics/).
 */
export function isGatewayMode(): boolean {
    if (typeof document === 'undefined') {
        return false; // SSR or test environment
    }
    try {
        const baseUrl = new URL(document.baseURI);
        const pathname = baseUrl.pathname.replace(/\/+$/, '');
        return pathname === '/analytics' || pathname.startsWith('/analytics/');
    } catch {
        return false;
    }
}

const GATEWAY_PATHS = {
    APP_URL: '/analytics',
    BFC_URL: '/home',
    STUDIO_URL: '/studio'
} as const;

type GatewayUrls = {
    APP_URL: string;
    BFC_URL: string;
    STUDIO_URL: string;
};

type EnvironmentConfig = { standalone: GatewayUrls; gatewayBase: string };

const SANDBOX_CONFIG: EnvironmentConfig = {
    standalone: {
        APP_URL: 'https://sandbox-analytics.bannerflow.com',
        BFC_URL: 'https://sandbox-home.bannerflow.com',
        STUDIO_URL: 'https://sandbox-studio.bannerflow.com'
    },
    gatewayBase: 'https://sandbox-app.bannerflow.com'
};

const ENVIRONMENT_URLS: Record<Stage, EnvironmentConfig> = {
    production: {
        standalone: {
            APP_URL: 'https://analytics.bannerflow.com',
            BFC_URL: 'https://home.bannerflow.com',
            STUDIO_URL: 'https://studio.bannerflow.com'
        },
        gatewayBase: 'https://app.bannerflow.com'
    },
    sandbox: SANDBOX_CONFIG,
    remote: SANDBOX_CONFIG,
    local: {
        standalone: {
            APP_URL: 'https://analytics.bannerflow.local',
            BFC_URL: 'https://home.bannerflow.local',
            STUDIO_URL: 'https://studio.bannerflow.local'
        },
        gatewayBase: 'https://app.bannerflow.local'
    }
};

/**
 * Returns the appropriate URLs based on the environment stage and gateway mode.
 * In gateway mode, URLs point to subpaths on the unified app domain.
 * In standalone mode, URLs point to dedicated subdomains.
 */
export function getGatewayUrls(stage: Stage): GatewayUrls {
    const config = ENVIRONMENT_URLS[stage];

    if (!config) {
        throw new Error(`Unknown environment stage: ${stage}`);
    }

    if (!isGatewayMode()) {
        return config.standalone;
    }

    return {
        APP_URL: `${config.gatewayBase}${GATEWAY_PATHS.APP_URL}`,
        BFC_URL: `${config.gatewayBase}${GATEWAY_PATHS.BFC_URL}`,
        STUDIO_URL: `${config.gatewayBase}${GATEWAY_PATHS.STUDIO_URL}`
    };
}
